import { Icon as NacreIcon, WeezHeader, Dropdown, DropdownItem, ButtonDropdown, Button, Tooltip, Statistics, Numbers, Chip, ToastContext, Message, Toggle } from '@weezevent/nacre';

import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSegment, useTrackingPage } from '../../components/tracking/trackingProvider';
import useMixpanel from '../../context/useMixpanel';

import { List } from 'semantic-ui-react';
import RateGroup from '../../models/rateGroup';
import Loading from '../../components/loading';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import './rates.css';
import RateTemplateTooltip from './rateTemplateTooltip';
import { NewGroupAction } from './components/action/newGroupAction';
import { RateStatus } from './components/rateStatus';
import EventSettings from '../../models/eventSettings';
import { RateCategorySeat } from './components/rateCategorySeat';
import ModalSettingsEvent from './components/modalSettingsEvent';
import { RateBundleInfo } from './components/rateBundleInfo';
import { RateMembershipInfo } from './components/rateMembershipInfo';
import { ModalQuotaEvent } from './components/modalQuotaEvent';
import { formatDateTime } from '../../utils/date';
import Messages from './components/messages';
import ExportButton from '../../components/export/exportButton';
import Styles from '../../styles/rate/rateList.module.css';

import RatesManager, { generateClassNameRateName } from './components/rateManager';
import StyleRM from './components/rateManager.module.css';
import { compute_sum_float } from '../../pages/rates/utils';
import ModalSalesEndHourSessions from './components/modalSalesEndHourSessions';
import QuotasModel from '../../models/quotasModel';
import { fetcher } from '@weezevent/weezjs-core';
import RateModel from '../../models/rateModel';

function StatisticsCTA({ onClick = () => {}, label, iconName = null }) {
    return (
        <div className={Styles['rate-header-card-cta']} onClick={onClick}>
            <span>{label}</span>
            {iconName && (
                <NacreIcon
                    className={`${Styles['rate-header-card-cta-icon']} ${iconName === 'edit' ? Styles['rate-header-card-cta-icon-edit'] : ''}`}
                    size={iconName === 'eye' ? 'small' : 'regular'}
                    name={iconName}
                />
            )}
        </div>
    );
}

StatisticsCTA.propTypes = {
    onClick: PropTypes.func,
    label: PropTypes.string.isRequired,
    iconName: PropTypes.string
};

function RatesExportButton({ organization, eventID, isSeatedEvent = false, toast }) {
    const { t } = useTranslation();

    const exportColumns = React.useMemo(() => {
        const baseExportColumns = [
            { target: 'id', value_map: null, header: t('ticket.export.headers.id') },
            { target: 'name', value_map: null, header: t('ticket.export.headers.name') },
            { target: 'type', value_map: null, header: t('ticket.export.headers.type') },
            { target: 'total_price', value_map: null, header: t('ticket.export.headers.paid_price') },
            { target: 'fees_sum', value_map: null, header: t('ticket.export.headers.fees_amount_ttc') },
            { target: 'quantity', value_map: null, header: t('ticket.export.headers.quota') },
            { target: 'group_id', value_map: null, header: t('ticket.export.headers.group_id') },
            { target: 'group_name', value_map: null, header: t('ticket.export.headers.group') },
            { target: 'form_ids', value_map: null, header: t('ticket.export.headers.forms_id') }
        ];

        if (isSeatedEvent) {
            const seatedExtraExportColumns = [
                { target: 'inventory_ids', value_map: null, header: t('ticket.export.headers.inventories_id') },
                { target: 'inventory_names', value_map: null, header: t('ticket.export.headers.inventories') },
                { target: 'category_id', value_map: null, header: t('ticket.export.headers.category_id') },
                { target: 'category_name', value_map: null, header: t('ticket.export.headers.category') },
                { target: 'contingent_ids', value_map: null, header: t('ticket.export.headers.contingents_id') },
                { target: 'contingent_names', value_map: null, header: t('ticket.export.headers.contingents') }
            ];
            return baseExportColumns.concat(seatedExtraExportColumns);
        } else {
            return baseExportColumns;
        }
    }, [isSeatedEvent]);

    return (
        <ExportButton
            path={`/ticket/organizations/${organization.id}/events/${eventID}/rates/export/`}
            showButton={false}
            choiceProcess={'rates-excel'}
            onError={() => toast.error(t('ticket.export.download_state_error'))}
            matchingData={exportColumns}
            buttonTrans={'ticket.rates.action.export.button'}
            organization={organization}
        />
    );
}

function HeaderCard({ rates, event, organization, settings, syncSettings, onOpenWidget, quotas, sellingRates }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const locale = i18n.language;
    const [showModal, setShowModal] = React.useState(false);
    const [showModalQuota, setShowModalQuota] = React.useState(false);
    const [showSalesEndSessionsModal, setShowSalesEndSessionsModal] = React.useState(false);

    const quotaGlobal = React.useMemo(() => {
        return quotas.find(q => !q.rate_id && !q.session_id && !q.rate_group_id);
    }, [quotas]);

    const titleStatSalePeriode = React.useMemo(() => {
        if (event.use_sessions) {
            const salesEndHour = Number(settings.values.sales_end_hour_relative);
            if (salesEndHour > 0) {
                return t('ticket.rates.modalSalesEndHourSessions.statAfterBeginningSessionTitle', { salesEndHour: Math.abs(salesEndHour) });
            }

            if (salesEndHour < 0) {
                return t('ticket.rates.modalSalesEndHourSessions.statBeforeBeginningSessionTitle', { salesEndHour: Math.abs(salesEndHour) });
            }

            return t('ticket.rates.modalSalesEndHourSessions.statBeginningSessionTitle');
        }

        if (settings.start_sale && settings.end_sale) {
            return t('ticket.rates.modalSettingsEvent.onSaleBetween', {
                startSale: formatDateTime(settings.start_sale, event.timezone, locale),
                endSale: formatDateTime(settings.end_sale, event.timezone, locale)
            });
        }

        if (settings.start_sale && !settings.end_sale) {
            return t('ticket.rates.modalSettingsEvent.onSaleFromDate', {
                startSale: formatDateTime(settings.start_sale, event.timezone, locale)
            });
        }

        if (!settings.start_sale && settings.end_sale) {
            return t('ticket.rates.modalSettingsEvent.onSaleUntilDate', {
                endSale: formatDateTime(settings.end_sale, event.timezone, locale)
            });
        }

        return t('ticket.rates.modalSettingsEvent.onSaleUntilTheEnd');
    }, [settings, t, event, locale]);

    const settingsQuota = React.useMemo(() => {
        const value = settings.quota;
        const limited = Boolean(Number(settings.is_limited_quota));
        const quotaLimited = limited ? <Numbers value={value} locale={locale} /> : t('ticket.rates.modalQuotaEvent.unlimited');

        const quotaTaken = quotaGlobal?.quota_taken;
        const sellingTotal = sellingRates?.total;
        const countSales = quotaTaken - sellingTotal;

        return (
            <div className={Styles['remaining_quotas']}>
                <Numbers value={countSales} locale={locale} /> / {quotaLimited}
                {sellingTotal > 0 && <Chip text={t('ticket.rates.modalQuotaEvent.in_progress', { count: sellingTotal })} />}
            </div>
        );
    }, [settings, event, quotaGlobal, sellingRates, t, locale]);

    return (
        <div>
            {!event.use_sessions && showModal && (
                <ModalSettingsEvent
                    event={event}
                    organization={organization}
                    settings={settings}
                    showModal={showModal}
                    onSuccess={() => {
                        syncSettings({ allow_cache: false, cache: false });
                    }}
                    onCloseModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
            {event.use_sessions && showSalesEndSessionsModal && (
                <ModalSalesEndHourSessions
                    event={event}
                    organization={organization}
                    settings={settings}
                    showModal={showSalesEndSessionsModal}
                    onSuccess={() => {
                        syncSettings({ allow_cache: false, cache: false });
                    }}
                    onCloseModal={() => {
                        setShowSalesEndSessionsModal(false);
                    }}
                />
            )}
            {!event.use_sessions && showModalQuota && (
                <ModalQuotaEvent
                    event={event}
                    organization={organization}
                    settings={settings}
                    showModal={showModalQuota}
                    quotaGlobal={quotaGlobal}
                    onSuccess={() => {
                        syncSettings({ allow_cache: false, cache: false });
                    }}
                    onCloseModal={() => {
                        setShowModalQuota(false);
                    }}
                />
            )}

            <Statistics.Group className={Styles[`${!event.use_sessions ? 'rate-list-group-statistics' : 'rate-list-group-statistics-event-sessions'}`]}>
                {!event.use_sessions && (
                    <Statistics
                        subtitle={t('ticket.rates.modalQuotaEvent.subtitleCard')}
                        helpLabel={t('ticket.rates.modalQuotaEvent.quotaTooltip')}
                        stat={<span style={{ fontSize: '16px' }}>{settingsQuota}</span>}
                        footer={
                            <StatisticsCTA
                                onClick={() => {
                                    setShowModalQuota(true);
                                    track('ACTION > MODIFY GLOBAL QUOTA', { 'Page Title': 'TICKET TYPES' });
                                }}
                                label={t('ticket.common.action.edit')}
                                iconName={'edit'}
                            />
                        }
                    />
                )}
                <Statistics
                    className={Styles['statistics-sales-period']}
                    helpLabel={!event.use_sessions ? t('ticket.rates.modalSettingsEvent.salesPeriodeTooltip') : t('ticket.rates.modalSettingsEvent.salesPeriodeTooltipSessions')}
                    subtitle={t('ticket.rates.modalSettingsEvent.salesPeriode')}
                    stat={<span style={{ fontSize: '16px', fontWeight: 'normal' }}>{titleStatSalePeriode}</span>}
                    footer={
                        <StatisticsCTA
                            onClick={() => {
                                if (!event.use_sessions) setShowModal(true);
                                else setShowSalesEndSessionsModal(true);
                                track('ACTION > MODIFY END EVENT', { 'Page Title': 'TICKET TYPES' });
                            }}
                            label={t('ticket.common.action.edit')}
                            iconName={'edit'}
                        />
                    }
                    size={'medium'}
                />
                <Statistics
                    helpLabel={t('ticket.rates.currentlySellingTooltip')}
                    subtitle={t('ticket.rates.currentlySelling')}
                    stat={<span style={{ fontSize: '16px' }}>{` ${rates.filter(rate => !rate.disabled).length} / ${rates.length}`}</span>}
                    footer={
                        <StatisticsCTA
                            label={t('ticket.rates.displayWidget')}
                            onClick={() => {
                                onOpenWidget();
                                track('ACTION > DISPLAY WIDGET', { 'Page Title': 'TICKET TYPES' });
                            }}
                            iconName={'eye'}
                        />
                    }
                />
            </Statistics.Group>
        </div>
    );
}

HeaderCard.propTypes = {
    event: PropTypes.object.isRequired,
    rates: PropTypes.array.isRequired,
    organization: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    syncSettings: PropTypes.func.isRequired,
    onOpenWidget: PropTypes.func.isRequired,
    quotas: PropTypes.array.isRequired,
    sellingRates: PropTypes.object
};

export function InheritedProperty({ object, template, property, handleProperty, displayIcon = false }) {
    const displayTooltip = React.useMemo(() => {
        if (!displayIcon) {
            return false;
        }
        let _object = object.object || object;
        for (const k in _object) {
            if (object[k] === null && object?.[template]?.[k] !== null && object?.[template]?.[k] !== undefined) {
                return true;
            }
        }
    }, [object, template, property, displayIcon]);

    if (object[property]) {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{handleProperty(object[property])}</span>
                {displayTooltip && <RateTemplateTooltip template={object[template]} toRatePage={Boolean(template === 'rate_template')} />}
            </div>
        );
    }

    if (!object?.[template]?.[property]) {
        return '-';
    }

    return (
        <div className="inherited-property-container">
            <span className="inherited-property">{handleProperty(object[template][property])}</span>
            {displayTooltip && <RateTemplateTooltip template={object[template]} toRatePage={Boolean(template === 'rate_template')} />}
        </div>
    );
}

InheritedProperty.propTypes = {
    object: PropTypes.object.isRequired,
    property: PropTypes.string.isRequired,
    handleProperty: PropTypes.func.isRequired,
    template: PropTypes.string.isRequired,
    displayIcon: PropTypes.bool
};

function ToggleVisibility({ rate, event, organizationId, toast, t, mixpanel, track }) {
    const [isSubmittingVisibility, setIsSubmittingVisibility] = React.useState(false);

    const onToggleVisibility = React.useCallback(
        rate => {
            setIsSubmittingVisibility(true);

            if (rate instanceof RateModel) {
                //add key form_id , so that the backend does not delete the linked form
                rate.form_id = rate.form;
            }

            rate.relays = (rate.relays || []).map(relay => (typeof relay === 'number' ? relay : relay?.id));

            rate.disabled = !rate.disabled;

            rate.save({
                organizationId,
                eventId: event.pk
            })
                .then(() => {
                    const action = rate.disabled ? 'ACTION > HIDE RATE' : 'ACTION > DISPLAY RATE';
                    mixpanel.track(action, {
                        'Rate ID': rate.id,
                        'Rate Name': rate.name
                    });
                    toast.success(t('ticket.rates.editVisibilitySuccess'));
                })
                .catch(() => {
                    toast.error(t('ticket.rates.editVisibilityError'));
                    rate.disabled = !rate.disabled; // Rollback visibility toggle
                })
                .finally(() => {
                    setIsSubmittingVisibility(false);
                });

            track('ACTION > VISIBILITY', { 'Page Title': 'TICKET TYPES' });
        },
        [organizationId, event, toast, t, mixpanel, track]
    );

    return <Toggle checked={!rate.disabled} onChange={() => onToggleVisibility(rate)} disabled={isSubmittingVisibility} />;
}

export default function RatesList({ event, organization, menhirUrl }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const mixpanel = useMixpanel();
    const locale = i18n.language;

    const [rates, setRates] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [sellingRates, setSellingRates] = React.useState(null);
    // get eventSetting with null id because Model is modified to perform get action on list route.
    const [settings, loading, syncSettings] = EventSettings.useApiModel(
        {
            id: null,
            organizationId: organization.id,
            eventId: event.id
        },
        [organization, event]
    );

    const [quotas, loadingQuotas] = QuotasModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            getForRates: true,
            cache: false,
            allow_cache: false
        },
        [organization, event]
    );

    function quotaTakenByItem(items, quotas, quotaItemKey) {
        const ids = items.map(i => i.id);
        const itemQuotas = quotas.filter(q => ids.includes(q[quotaItemKey]));
        return itemQuotas.reduce((acc, q) => {
            if (acc[q[quotaItemKey]] === undefined) {
                acc[q[quotaItemKey]] = 0;
            }

            acc[q[quotaItemKey]] += q.quota_taken;
            return acc;
        }, {});
    }

    const quotaTakenByRate = React.useMemo(() => {
        if (loadingQuotas || !rates.length) {
            return {};
        }
        return quotaTakenByItem(rates, quotas, 'rate_id');
    }, [quotas, rates, loadingQuotas]);

    const quotaTakenByGroup = React.useMemo(() => {
        if (loadingQuotas || !groups.length) {
            return {};
        }
        return quotaTakenByItem(groups, quotas, 'rate_group_id');
    }, [quotas, groups, loadingQuotas]);

    const [reloadList, setReloadList] = React.useState(null);

    const toast = React.useContext(ToastContext);
    const history = useHistory();

    const organizationId = organization.id;

    React.useEffect(() => {
        fetcher
            .get(`/ticket/organizations/${organization.id}/events/${event.id}/rates-in-progress`)
            .then(({ response }) => {
                setSellingRates(response);
            })
            .catch(e => {
                console.log(e);
            });
    }, []);

    useTrackingPage('VIEW - TICKET TYPES', { 'Page Title': 'TICKET TYPES' });

    let tableCells = [
        {
            header: t('ticket.rates.rateName'),
            renderRate: (rate, { categories, groups }) => {
                return (
                    <InheritedProperty
                        displayIcon={true}
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                <RateCategorySeat event={event} rate={rate} categories={categories} />
                                {name}
                                <RateBundleInfo bundleSize={rate.bundle_size || 1} />
                                <RateMembershipInfo rate={rate} />
                            </div>
                        )}
                        template="rate_template"
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <NacreIcon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <NacreIcon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                        <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                    </div>
                );
            }
        },
        {
            header: t('ticket.rates.price'),
            renderRate: (rate, { event }) => {
                let _price = rate.rate_template?.price || rate.price;
                const computedPrice = rate.fees?.length && rate.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), parseFloat(_price || 0));
                return (
                    <InheritedProperty
                        property="price"
                        object={rate}
                        handleProperty={price =>
                            price !== '0.00' ? <Numbers value={computedPrice || price} locale={i18n.language} currency={event.currency} showNullDecimal /> : t('ticket.rates.free')
                        }
                        template="rate_template"
                    />
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.rates.sales'),
            renderRate: (rate, { event }) => {
                const sellingRatesQuota = sellingRates && sellingRates.rates && sellingRates.rates[rate.id] ? sellingRates.rates[rate.id] : 0;
                const quotaTaken = quotaTakenByRate[rate.id] || 0;

                return (
                    <div className={StyleRM['rates-table-taken']}>
                        <span>
                            <Numbers value={quotaTaken - sellingRatesQuota || 0} locale={locale} />
                        </span>
                        {sellingRatesQuota !== 0 && (
                            <Tooltip triggerOnHover title={t('ticket.rates.sellingRatesTooltip')}>
                                <Chip text={`+${sellingRatesQuota}`} />
                            </Tooltip>
                        )}
                    </div>
                );
            },
            renderGroup: group => {
                const sellingGroupQuota = sellingRates && sellingRates.rate_groups && sellingRates.rate_groups[group.id] ? sellingRates.rate_groups[group.id] : 0;
                const quotaTaken = quotaTakenByGroup[group.id] || 0;

                return (
                    <div className={StyleRM['rates-table-taken']}>
                        <span>
                            <Numbers value={quotaTaken - sellingGroupQuota || 0} locale={locale} />
                        </span>
                        {sellingGroupQuota !== 0 && <Chip text={`+${sellingGroupQuota}`} />}
                    </div>
                );
            }
        },
        {
            quota: true,
            header: t('ticket.rates.quota'),
            renderRate: rate => {
                if (rate.quantity === 0) {
                    return '-';
                }
                return <Numbers value={rate.quantity} locale={locale} />;
            },
            renderGroup: group => {
                if (group.quota === 0) {
                    return '-';
                }
                return <Numbers value={group.quota} locale={locale} />;
            }
        },
        {
            seating: true,
            header: event?.isFakeSeatingMultipleInventories() ? t('ticket.eventDashboard.date') : t('ticket.rates.inventories'),
            renderRate: (rate, { inventories }) => {
                return (
                    <List>
                        {' '}
                        {/* Filter inventories where this rate belongs*/}
                        {rate.inventories
                            .map(rateInventory => {
                                return inventories
                                    .filter(inventory => inventory.uuid === rateInventory)
                                    .map((element, indice) => (
                                        <List.Item style={{ padding: '0' }} key={indice}>
                                            {' '}
                                            {element.name}{' '}
                                        </List.Item>
                                    ));
                            })
                            .flat(Infinity)}
                    </List>
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.rates.visibility'),
            disabledOnClick: true,
            renderRate: (rate, { event }) => {
                if (rate.distributor) {
                    // Rate from webservices
                    return (
                        <Tooltip triggerOnHover={true} title={t(`ticket.rates.webservicesVisibility`)}>
                            <NacreIcon className={Styles['disabled-icon']} name="eye-slash" size="small" />
                        </Tooltip>
                    );
                }
                return <ToggleVisibility rate={rate} event={event} organizationId={organizationId} toast={toast} t={t} mixpanel={mixpanel} track={track} />;
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.rates.status.title'),
            renderRate: (rate, { event, rates }) => {
                if (rate.distributor) {
                    // Rate from webservices
                    return rate.distributor.label;
                }
                return <RateStatus rate={rate} ratesList={rates} settings={settings} event={event} quotas={quotas} />;
            },
            renderGroup: () => {
                return null;
            }
        }
    ];

    const urlWidget = React.useCallback(() => {
        return window.open(`${window.weezConfig.api.widgetUrl}/ticket/E${event.id}/?code=${event.default_widget_access_code}#weez_preview`);
    }, [event]);

    const onReloadList = React.useCallback(func => {
        setReloadList(() => func);
    }, []);

    if (!rates || loading || loadingQuotas) {
        return <Loading />;
    }

    return (
        <>
            <WeezHeader
                title={t('ticket.rates.pageTitle')}
                dropdownProps={{ renderWhenClosed: true }}
                rightComponent={[
                    <Button
                        key={'widget'}
                        onClick={() => {
                            urlWidget();
                            track('ACTION > PREVIEW RATE', { 'Page Title': 'TICKET TYPES' });
                        }}
                        label={
                            <div className={Styles['rate-list__header-button-widget-container']}>
                                <div>
                                    <NacreIcon className={Styles['rate-list__header-button-widget-icon']} name={'eye'} />
                                </div>
                                <div>{t('ticket.rates.displayWidget')}</div>
                            </div>
                        }
                        className={Styles['rate-list-widget-button']}
                    />,
                    <ButtonDropdown
                        key={'dropdown_add'}
                        renderWhenClosed
                        trigger={
                            <Button
                                primary
                                icon={<NacreIcon name="plus" />}
                                onClick={() => track('ACTION > CREATE RATE', { 'Page Title': 'TICKET TYPES' })}
                                label={t('ticket.common.action.add')}
                            />
                        }
                        items={[
                            <Dropdown.Item
                                key="0"
                                item={t('ticket.rates.addRate')}
                                onClick={() => {
                                    history.push(`/ticket/O${organizationId}/E${event.id}/settings/rates/new`);
                                    track('ACTION > ADD RATE', { 'Page Title': 'TICKET TYPES' });
                                }}
                            />,
                            <NewGroupAction key="1" groups={groups} model={RateGroup} onSave={() => reloadList()} />
                        ]}
                    />,
                    <ButtonDropdown
                        className={Styles['dropdown-button-margin-left']}
                        key={'dropdown'}
                        onOpen={() => track('ACTION > ACTION MANAGE TICKET TYPES', { 'Page Title': 'MANAGE TICKET TYPES' })}
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<NacreIcon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <RatesExportButton key="0" eventID={event.id} organization={organization} isSeatedEvent={event.isSeatedForRates()} toast={toast} />,
                            <DropdownItem
                                key={'widget'}
                                onClick={() => {
                                    urlWidget();
                                    track('ACTION > PREVIEW RATE', { 'Page Title': 'TICKET TYPES' });
                                }}
                                item={t('ticket.rates.displayWidget')}
                                className={Styles['rate-list-widget-button-alt']}
                            />
                        ]}
                    />
                ]}
            >
                {t('ticket.rates.subtitle')}
            </WeezHeader>
            {rates.length >= 800 && (
                <Message type={'warning'} title={t('ticket.common.warning')}>
                    {t(`ticket.rates.messageWarningLimitRates`)}
                </Message>
            )}
            <Messages event={event} settings={settings} rates={rates} />
            <HeaderCard
                rates={rates}
                organization={organization}
                event={event}
                settings={settings}
                syncSettings={syncSettings}
                onOpenWidget={urlWidget}
                quotas={quotas}
                sellingRates={sellingRates}
            />
            <RatesManager
                event={event}
                organization={organization}
                initialQuery={{
                    organizationId,
                    eventId: event.id
                }}
                tableCells={tableCells}
                onGroupsLoaded={setGroups}
                onRatesLoaded={setRates}
                onReloadList={onReloadList}
                quotaTakenByRate={quotaTakenByRate}
                quotaTakenByGroup={quotaTakenByGroup}
                settings={settings}
            />
        </>
    );
}

RatesList.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    menhirUrl: PropTypes.string.isRequired
};
